import {
    HttpClient,
    HttpErrorResponse,
    HttpEvent,
    HttpHandlerFn,
    HttpRequest,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Global } from 'constants/global.const';
import { makeUrl } from 'helpers/makeUrl';
import { isNull } from 'helpers/nullCheck';
import { Security, User } from 'models/user.model';
import { BehaviorSubject, catchError, filter, Observable, switchMap, take, throwError } from 'rxjs';
import { StorageService } from './storage.service';

@Injectable({
	providedIn: 'root',
})
export class AuthService {
	private readonly http = inject(HttpClient);
	private readonly router = inject(Router);
	private readonly storageService = inject(StorageService);

	private isRefreshing = false;
	private refreshToken$ = new BehaviorSubject<Security | null>(null);

	public isAuthenticated() {
		const isAuth = this.storageService.getItem<Security>('loginAuth');
		const userInfo = this.storageService.getItem<User>('userInfo');
		if (isNull(userInfo)) return false;
		return !!isAuth;
	}

	public getAuthInfo(): Security | undefined {
		try {
			const auth = this.storageService.getItem<Security>('loginAuth');
			return auth ?? undefined;
		} catch (error) {
			console.error('Erro | authService | getAuthInfo', error);
			throw error;
		}
	}

	public hasInvalidAccess(): boolean {
		const auth = this.getAuthInfo();
		return !auth?.token || !auth?.id || this.getTimeLeft(auth.refreshTokenExpires) <= 10;
	}

	private getTimeLeft(expires: string | number): number {
		return Number(expires) - Date.now() / 1000;
	}

	public needsRefresh(): boolean {
		const auth = this.getAuthInfo();

		const tokenTimeLeft = this.getTimeLeft(auth!.tokenExpires);
		const refreshTimeLeft = this.getTimeLeft(auth!.refreshTokenExpires);
		return tokenTimeLeft < 60 && refreshTimeLeft > 0;
	}

	public handleRefresh(
		req: HttpRequest<unknown>,
		next: HttpHandlerFn,
	): Observable<HttpEvent<unknown>> {
		const auth = this.getAuthInfo();

		if (!this.isRefreshing) {
			this.isRefreshing = true;
			this.refreshToken$.next(null);

			return this.refreshToken(auth!.refreshToken!).pipe(
				switchMap((newAuth) => {
					this.isRefreshing = false;
					this.refreshToken$.next(newAuth);
					return next(req);
				}),
				catchError((err) => {
					this.isRefreshing = false;
					return throwError(() => err);
				}),
			);
		}

		return this.refreshToken$.pipe(
			filter((token) => token !== null),
			take(1),
			switchMap(() => next(req)),
		);
	}

	private refreshToken(refreshToken: string): Observable<Security> {
		return this.http
			.get<Security>(makeUrl(Global.services.user.refreshtoken()), {
				params: { refreshToken },
				headers: { Authorization: `Bearer ${refreshToken}` },
			})
			.pipe(
				switchMap((res) => {
					if (!res?.token || !res?.id) throw new Error('Invalid response');
					this.storageService.setItem('loginAuth', res);
					return [res];
				}),
				catchError(() => {
					this.logout();
					return throwError(() => new HttpErrorResponse({ status: 401 }));
				}),
			);
	}

	public logout() {
		this.storageService.clear();
		const currentUrl = window.location.pathname + window.location.search;
		this.router.navigate(
			['/login'],
			currentUrl !== '/login' ? { queryParams: { redirect: currentUrl } } : {},
		);
	}
}
